import { SiteUrls } from '@tableau/shared-urls';
import _ from 'underscore';

import { WindowLocationService } from '~commonInfra/services/WindowLocationService';
import { UrlParamKeys } from '~commonInfra/url/UrlParamKeys';

import { QueryParameterHelper } from '../../../viz/helpers/QueryParameterHelper';
import { FilterFieldNames } from '../../models/IFilterClause';

// Return the query parameters that are in the hash as an object
export function getQueryParameters(): any {
  const search: any = WindowLocationService.getHashQueryObject();
  return search;
}

// Add the current query parameters that are in the hash to the given url
export function addQuery(url: string, omitOrderQueryParam: boolean = false, omitContentTypeQueryParam: boolean = false): string {
  let params = getQueryParameters();
  if (omitOrderQueryParam) {
    params = _.omit(params, UrlParamKeys.Order);
  }
  if (omitContentTypeQueryParam) {
    params = _.omit(params, FilterFieldNames.contentType);
  }
  return QueryParameterHelper.urlWithParams(url, params);
}

// TODO: This subclass should be removed.
// The remaining methods should either be moved to SiteUrls
// or to separate helpers. We should do that work when removing the mixedContentEnabled feature flag.

export class SpacesSiteUrls extends SiteUrls {

  constructor(config: { siteUrlName: string, queryString?: string }) {
    super({
      siteUrlName: config.siteUrlName,
      // Many existing call sites expect queryString to be filled in automatically
      queryString: config.queryString != null ? config.queryString : window.location.search,
    });
  }

  //
  // URLs go below here
  //

  public exploreSiteDataconnectionsUrl(): string {
    return this.datasourcesUrl() + '?' + FilterFieldNames.contentType + '=' + 'dataconnection';
  }

  public exploreDataConnectionUrl(dataConnectionId: string) {
    // This URL navigates to the data source by its Unified Datasource (connection) ID
      return this.datasourceDataConnectionUrl(dataConnectionId);
  }

  public userDataconnectionsUrl(username: string, domainName: string) {
    const url = this.userDatasourcesUrl(username, domainName);
    return url + '?' + FilterFieldNames.contentType + '=' + 'dataconnection';
  }

  public contentRootUrl(): string {
    return this.homeUrl();
  }

  public contentRootPath(): string {
    return this.homePath();
  }

  public exploreVconnConnectionUrl(dataconnection: string): string{
    return this.virtualConnectionGrantAccessUrl(dataconnection);
  }

  public datasourceDependentDataSourcesUrl(datasourceId: string): string {
    return this.datasourceUrl(datasourceId) + '/dependentDataSources';
  }

  public datasourceDependentDataSourcesPath(datasourceId: string): string {
    return this.datasourcePath(datasourceId) + '/dependentDataSources';
  }

  // The pod reverse-proxy strips the /site/<name> prefix before forwarding, so
  // TK sees the same /app tail either way and resolves the site from the session.
  public tableauKnowledgePath(): string {
    return `${this.defaultPath()}knowledge/app`;
  }

  // Redirect for a favorite whose type has no type-specific redirect in @tableau/shared-urls.
  // Defined locally for the same reason as the Content Relay helpers below.
  public redirectToFavoritePath(contentType: string, id: string): string {
    return `${this.defaultPath()}redirect_to_favorite/${contentType}/${id}`;
  }

  public redirectToFavoriteUrl(contentType: string, id: string): string {
    return `${this.defaultUrl()}redirect_to_favorite/${contentType}/${id}`;
  }

  //
  // Content Relay (content mobility plans)
  //
  // NOTE: These are defined locally rather than in @tableau/shared-urls (where the collection*
  // helpers live) because the private fullUrl()/sitePath() helpers aren't accessible from a
  // subclass. They are composed from the public defaultPath()/defaultUrl() (which both end in the
  // site prefix + '/'). If Content Relay graduates beyond the feature flag, these should move to
  // shared-urls to match the Collections precedent. See W-23846068.
  public contentRelayPlansPath(): string {
    return `${this.defaultPath()}contentRelayPlans`;
  }

  public contentRelayPlansUrl(): string {
    return `${this.defaultUrl()}contentRelayPlans`;
  }

  public allContentRelayPlansPath(): string {
    return `${this.contentRelayPlansPath()}/all`;
  }

  public allContentRelayPlansUrl(): string {
    return `${this.contentRelayPlansUrl()}/all`;
  }

  public contentRelayPlanPath(luid: string): string {
    return `${this.contentRelayPlansPath()}/${luid}`;
  }

  public contentRelayPlanUrl(luid: string): string {
    return `${this.contentRelayPlansUrl()}/${luid}`;
  }

  // Run History tab of a Content Relay plan's detail page (route segment `executions`).
  public contentRelayPlanExecutionsUrl(luid: string): string {
    return `${this.contentRelayPlanUrl(luid)}/executions`;
  }

  // Import history tab of the Content Relays section.
  public contentRelayImportsPath(): string {
    return `${this.contentRelayPlansPath()}/imports`;
  }

  public contentRelayImportsUrl(): string {
    return `${this.contentRelayPlansUrl()}/imports`;
  }

}
